import { useMemo, useState } from 'react';
import { TZDate } from 'react-day-picker';
import { getScoreLevel } from '../../components/AnalysisLayout/analysis.utils';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { dateRangePickerTimeZone, type DateRange } from '../../components/DateRangePicker/DateRangePicker';
import { DateUtils } from '../../utils/DateUtils';
import { ValidationUtils } from '../../utils/ValidationUtils';
import { useGetPastSurveys } from '../../hooks/queries/useGetPastSurveys';
import { useGetTeamHealthMetricsQuery } from '../../hooks/queries/useGetTeamHealthMetricsQuery/useGetTeamHealthMetricsQuery';
import { useGetSurveysWithinTimeframe } from '../../hooks/queries/useGetSurveysWithinTimeframe';
import { useGetScoreByQuestions } from '../../hooks/queries/useGetScoreByQuestions';
import { useGetQuestionTrends } from '../../hooks/queries/useGetQuestionTrends';
import { getFocusArea, toAreas, toQuestionRows } from './dashboard.utils';
import { LatestResult } from './components/LatestResult';
import { SurveyProcess } from './components/SurveyProcess';
import { AreaCards } from './components/AreaCards';
import { TrendSection } from './components/TrendSection';
import { QuestionTable } from './components/QuestionTable';
import { RecentSurveys } from './components/RecentSurveys';
import classes from './TeamDashboard.module.css';

const initialDateRange = (): DateRange => ({
  fromDate: new TZDate(DateUtils.getDateTodayMinusDays(365), dateRangePickerTimeZone).toISOString(),
  toDate: new TZDate(DateUtils.getDateTodayISO(), dateRangePickerTimeZone).toISOString(),
});

const toQueryDate = (date: string) => DateUtils.dateInISOFormat(new TZDate(date, dateRangePickerTimeZone));

/** Dashboard for teamleder: psykologisk trygghet for valgt team */
export const TeamDashboard = () => {
  const { selectedTeamId: teamId } = useSelectedUnitContext();
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);

  const pastSurveys = useGetPastSurveys(teamId);
  const metrics = useGetTeamHealthMetricsQuery(teamId);

  // Grafen, trendene og spørsmålene viser samme periode
  const { hasError: hasPeriodError, data: periodError } = ValidationUtils.validateDatePickerForm(dateRange);
  const from = toQueryDate(dateRange.fromDate);
  const to = toQueryDate(dateRange.toDate);
  const surveysInPeriod = useGetSurveysWithinTimeframe(from, to, teamId, !hasPeriodError);
  const questionScores = useGetScoreByQuestions(from, to, teamId, !hasPeriodError);
  const questionTrends = useGetQuestionTrends(from, to, teamId, !hasPeriodError);

  const areas = useMemo(() => toAreas(metrics.data ?? []), [metrics.data]);
  const questionRows = useMemo(
    () => toQuestionRows(questionScores.data ?? [], questionTrends.data?.questionTrends),
    [questionScores.data, questionTrends.data],
  );

  return (
    <>
      <div className={classes.top}>
        <LatestResult
          teamId={teamId}
          surveys={pastSurveys.data}
          focusArea={getFocusArea(areas)}
          allAreasGood={areas.length > 0 && areas.every((area) => getScoreLevel(area.score) === 'good')}
        />
        <SurveyProcess teamId={teamId} />
      </div>

      <div className={classes.panel}>
        <AreaCards areas={areas} isLoading={metrics.isLoading} error={metrics.error} />
        <TrendSection
          dateRange={dateRange}
          setDateRange={setDateRange}
          periodError={hasPeriodError ? periodError : undefined}
          surveys={surveysInPeriod}
          questionTrends={{
            data: questionTrends.data?.questionTrends,
            isLoading: questionTrends.isLoading,
            error: questionTrends.error,
          }}
        />
        <QuestionTable
          teamId={teamId}
          rows={questionRows}
          isLoading={!hasPeriodError && questionScores.isLoading}
          error={questionScores.error}
        />
        <RecentSurveys
          teamId={teamId}
          surveys={pastSurveys.data}
          isLoading={pastSurveys.isLoading}
          error={pastSurveys.error}
        />
      </div>
    </>
  );
};
